import { Link, router } from "@inertiajs/react"
import { Loader2, Pencil } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import { errorMessages } from "@/lib/form-errors"
import { dollarsFromCents } from "@/lib/money-format"

import type { EditOptions, WorkOrderDetail } from "./types"

/**
 * The subclass fields. WHICH fields is a server decision (a cleaning has a
 * reason and a product, a bad order has a defect and an OTMA type), so this
 * component renders whatever `detail.fields` lists rather than carrying a
 * per-type layout that has to be kept in sync with the models.
 *
 * One Edit toggle for the whole card, one PATCH on save: an operator correcting
 * a work order usually fixes two or three things at once, and three separate
 * round trips would mean three chances to leave the record half-corrected.
 */
const LABELS: Record<string, string> = {
  defect_category: "Defect category",
  defect_details: "Defect details",
  bad_order_location: "Bad order location",
  repair_location: "Repair location",
  otma_type: "OTMA type",
  shop_id: "Shop",
  mru_team_id: "MRU team",
  facility_id: "Facility",
  cost_cents: "Cost ($)",
  notes: "Notes",
  program_year: "Program year",
  sent_from: "Sent from",
  official_off_lease_date: "Official off-lease date",
  reason: "Reason",
  product: "Product",
  responsible_specialist: "Responsible specialist",
  completed_by: "Completed by",
}

const TEXTAREA_FIELDS = new Set(["defect_details", "notes"])

export function DetailsCard({
  detail,
  options,
  updatePath,
  canEdit,
}: {
  detail: WorkOrderDetail
  options: EditOptions
  updatePath: string
  canEdit: boolean
}) {
  const routes = useOrgRoutes()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [values, setValues] = useState<Record<string, string>>(() =>
    initialValues(detail),
  )
  const [errors, setErrors] = useState<string[]>([])

  function save() {
    if (saving) return
    setSaving(true)
    setErrors([])
    const payload: Record<string, string | null> = {}
    for (const field of detail.fields) {
      const value = values[field]
      if (value === "") {
        payload[field] = null
      } else if (DOLLAR_FIELDS.has(field)) {
        payload[field] = dollarsToCents(value)
      } else {
        payload[field] = value
      }
    }
    router.patch(
      updatePath,
      { work_order: payload },
      {
        preserveScroll: true,
        onSuccess: () => setEditing(false),
        // WITHOUT THIS A 422 IS SILENT. The server answers an out-of-order
        // timeline with validation errors and no flash; the card closed its
        // spinner, kept the edit open and said nothing, so an operator read a
        // rejected save as a saved one.
        onError: (received) => setErrors(errorMessages(received)),
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Details</CardTitle>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="mr-1 size-4" />
            Edit
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {detail.car && (
          <div className="flex flex-wrap items-center gap-2 border-b pb-3">
            <Link
              href={routes.carPath(detail.car.id)}
              className="text-base font-semibold hover:underline"
            >
              {detail.car.mark_number}
            </Link>
            {detail.car.fleet_code && (
              <Badge variant="outline">{detail.car.fleet_code}</Badge>
            )}
            {detail.car.car_type && (
              <Badge variant="secondary">
                {detail.car.car_type === "pressure" ? "HP" : "GP"}
              </Badge>
            )}
            {detail.car.lessor_name && (
              <span className="text-muted-foreground text-sm">
                {detail.car.lessor_name}
              </span>
            )}
            {detail.field_repair && <Badge>Field repair</Badge>}
          </div>
        )}

        <DwellStrip detail={detail} />

        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm">
          {detail.fields.map((field) => (
            <FieldRow
              key={field}
              field={field}
              detail={detail}
              options={options}
              editing={editing}
              value={values[field] ?? ""}
              onChange={(next) =>
                setValues((prev) => ({ ...prev, [field]: next }))
              }
            />
          ))}
        </dl>

        {errors.length > 0 && (
          <ul className="border-destructive/40 bg-destructive/5 text-destructive space-y-1 rounded-lg border p-3 text-sm">
            {errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}

        {editing && (
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setValues(initialValues(detail))
                setErrors([])
                setEditing(false)
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving && <Loader2 className="mr-1 size-4 animate-spin" />}
              Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/** The three dwell numbers an operator quotes on a call, without arithmetic. */
function DwellStrip({ detail }: { detail: WorkOrderDetail }) {
  const stats = [
    { label: "Since bad order", value: detail.days_since_bad_order },
    { label: "Waiting on dispo", value: detail.days_waiting_dispo },
    { label: "In shop", value: detail.days_in_shop },
  ].filter((stat) => stat.value != null)

  if (stats.length === 0) return null

  return (
    <div className="flex flex-wrap gap-4 border-b pb-3">
      {stats.map((stat) => (
        <div key={stat.label}>
          <p className="text-muted-foreground text-xs">{stat.label}</p>
          <p className="text-lg font-semibold tabular-nums">{stat.value}d</p>
        </div>
      ))}
    </div>
  )
}

function FieldRow({
  field,
  detail,
  options,
  editing,
  value,
  onChange,
}: {
  field: string
  detail: WorkOrderDetail
  options: EditOptions
  editing: boolean
  value: string
  onChange: (value: string) => void
}) {
  const label = LABELS[field] ?? field.replace(/_/g, " ")

  if (!editing) {
    return (
      <>
        <dt className="text-muted-foreground capitalize">{label}</dt>
        <dd className="min-w-0 break-words">
          {displayValue(field, detail, options)}
        </dd>
      </>
    )
  }

  return (
    <>
      <dt className="text-muted-foreground pt-2 capitalize">
        <Label htmlFor={`field-${field}`}>{label}</Label>
      </dt>
      <dd>
        <FieldEditor
          field={field}
          options={options}
          value={value}
          onChange={onChange}
        />
      </dd>
    </>
  )
}

function FieldEditor({
  field,
  options,
  value,
  onChange,
}: {
  field: string
  options: EditOptions
  value: string
  onChange: (value: string) => void
}) {
  const choices = choicesFor(field, options)

  if (choices) {
    // "—" is the clear-this-field option. Radix rejects an empty-string value,
    // so the sentinel is mapped back to "" on the way out.
    return (
      <Select
        value={value || "__none__"}
        onValueChange={(next) => onChange(next === "__none__" ? "" : next)}
      >
        <SelectTrigger size="sm" className="w-full" id={`field-${field}`}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">—</SelectItem>
          {choices.map((choice) => (
            <SelectItem key={choice.value} value={String(choice.value)}>
              {choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  if (TEXTAREA_FIELDS.has(field)) {
    return (
      <Textarea
        id={`field-${field}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
      />
    )
  }

  return (
    <Input
      id={`field-${field}`}
      type={inputType(field)}
      // Dollars and cents, so the field must accept them.
      step={DOLLAR_FIELDS.has(field) ? "0.01" : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-9"
    />
  )
}

function choicesFor(field: string, options: EditOptions) {
  switch (field) {
    case "defect_category":
      return options.defect_categories.map((v) => ({ value: v, label: v }))
    case "otma_type":
      return options.otma_types.map((v) => ({ value: v, label: v }))
    case "repair_location":
      return options.repair_locations.map((v) => ({ value: v, label: v }))
    case "reason":
      return options.cleaning_reasons.map((v) => ({ value: v, label: v }))
    case "shop_id":
      return options.shops
    case "mru_team_id":
      return options.mru_teams
    case "facility_id":
      return options.facilities
    default:
      return null
  }
}

function inputType(field: string): string {
  if (field.endsWith("_date") || field.endsWith("_on")) return "date"
  if (field === "cost_cents" || field === "program_year") return "number"
  return "text"
}

function displayValue(
  field: string,
  detail: WorkOrderDetail,
  options: EditOptions,
) {
  const raw = detail.values[field]
  if (raw == null || raw === "")
    return <span className="text-muted-foreground">—</span>

  const choices = choicesFor(field, options)
  if (choices) {
    const match = choices.find((c) => String(c.value) === String(raw))
    if (match) return match.label
  }

  if (field === "cost_cents") {
    return `$${(Number(raw) / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  return String(raw)
}

/**
 * `cost_cents` is stored — and named — in CENTS, but every human-facing surface
 * shows dollars ("$4,865.00"). The edit form used to load the raw integer, so
 * an operator who opened Edit and saved without touching anything turned
 * $4,865.00 into 486,500 cents-read-as-dollars: a silent 100x on the one field
 * that is money. Dollars in, cents out, in one place.
 */
const DOLLAR_FIELDS = new Set(["cost_cents"])

export function centsToDollars(raw: unknown): string {
  const cents = Number(raw)
  return Number.isFinite(cents) ? dollarsFromCents(cents) : ""
}

export function dollarsToCents(value: string): string | null {
  const dollars = Number(value)
  return Number.isFinite(dollars) ? String(Math.round(dollars * 100)) : null
}

function initialValues(detail: WorkOrderDetail): Record<string, string> {
  const out: Record<string, string> = {}
  for (const field of detail.fields) {
    const raw = detail.values[field]
    if (raw == null || raw === "") {
      out[field] = ""
    } else if (DOLLAR_FIELDS.has(field)) {
      out[field] = centsToDollars(raw)
    } else {
      out[field] = String(raw)
    }
  }
  return out
}
